import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { Link } from 'react-router-dom';
import { getUploadUrl } from '../utils/config';
import AppHeader from './AppHeader';
import FamilyTree from './FamilyTree';
import TimelineView from './TimelineView';
import './Dashboard.css';

const TREE_VIEW_STATE_KEY = 'familyTree.viewState';

function getInitialTreeViewState() {
  try {
    const saved = sessionStorage.getItem(TREE_VIEW_STATE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    // Ignore parse errors
  }
  return { zoom: 0.5, pan: { x: 0, y: 0 } };
}

function Dashboard() {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('tree'); // 'tree', 'grid', or 'timeline'
  const [treeViewState, setTreeViewState] = useState(getInitialTreeViewState);
  const { user } = useContext(AuthContext);
  const { members, relationships, loading, refreshMembers, refreshRelationships, memberDegrees, focalPersonId } = useData();

  // Persist tree view state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(TREE_VIEW_STATE_KEY, JSON.stringify(treeViewState));
  }, [treeViewState]);

  const handleRelativeAdded = async () => {
    await refreshMembers();
    await refreshRelationships();
  };

  if (loading) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="dashboard">
      <AppHeader showBackButton={false} />

      <div className="dashboard-content">
        <div className="actions-bar">
          <div className="view-toggle">
            <button
              onClick={() => setViewMode('tree')}
              className={`btn btn-view ${viewMode === 'tree' ? 'btn-primary' : 'btn-secondary'}`}
            >
              {t('dashboard.treeView')}
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`btn btn-view ${viewMode === 'grid' ? 'btn-primary' : 'btn-secondary'}`}
            >
              {t('dashboard.gridView')}
            </button>
            <button
              onClick={() => setViewMode('timeline')}
              className={`btn btn-view ${viewMode === 'timeline' ? 'btn-primary' : 'btn-secondary'}`}
            >
              {t('dashboard.timeline')}
            </button>
          </div>
          <Link to="/member/new" className="btn btn-primary btn-add-member">{t('dashboard.addPerson')}</Link>
        </div>

        {/* Error handling removed - silent failure with cached data */}

        {members.length === 0 ? (
          <div className="empty-state">
            <h2>{t('dashboard.noMembers')}</h2>
            <p>{t('dashboard.noMembersDescription')}</p>
            <Link to="/member/new" className="btn btn-primary">{t('dashboard.addFirstMember')}</Link>
          </div>
        ) : (
          <>
            {viewMode === 'tree' ? (
              <FamilyTree
                members={members}
                relationships={relationships}
                currentUser={user}
                onRelativeAdded={handleRelativeAdded}
                viewState={treeViewState}
                onViewStateChange={setTreeViewState}
              />
            ) : viewMode === 'timeline' ? (
              <TimelineView
                members={members}
                relationships={relationships}
                memberDegrees={memberDegrees}
                focalPersonId={focalPersonId}
                currentUser={user}
              />
            ) : (
              <div className="members-grid">
                {members.map(member => {
                  const initials = `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase();
                  const hash = (member.first_name + member.last_name).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                  const hue = hash % 360;
                  return (
                  <Link key={member.id} to={`/member/${member.id}`} className="member-card">
                    <div className="member-info">
                      {member.photo_url ? (
                        <img
                          src={getUploadUrl(member.photo_url)}
                          alt={`${member.first_name} ${member.last_name}`}
                          className="member-photo"
                        />
                      ) : (
                        <div
                          className="member-avatar"
                          style={{ backgroundColor: `hsl(${hue}, 45%, 65%)` }}
                        >
                          {initials}
                        </div>
                      )}
                      <h3>{member.first_name} {member.last_name}</h3>
                    </div>
                  </Link>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
